import { articlePath, articlesIn, SUPPORT_ARTICLES } from './articles';
import { BRAND_NAME, SITE_DOMAIN, SUPPORT_PHONE_LABEL } from './site';

const toLink = (article) => ({ label: article.title, to: articlePath(article.slug) });

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  {
    label: 'About Us',
    to: '/about-us',
    children: [
      { label: 'Contact Us', to: '/contact-us' },
      { label: 'Refund Policy', to: '/refund-policy' },
      { label: 'Terms & Conditions', to: '/terms-and-conditions' },
      { label: 'Privacy Policy', to: '/privacy-policy' },
    ],
  },
  { label: 'Family Tree Maker 2024', to: '/family-tree-maker-2024' },
  { label: 'Family Tree Maker 2019', to: '/family-tree-maker-2019' },
  {
    label: 'Ancestry Support',
    to: '/ancestry-support',
    children: articlesIn('ancestry').slice(0, 6).map(toLink),
  },
  {
    label: 'Support',
    to: '/support',
    children: [{ label: 'Support Center', to: '/support' }, ...SUPPORT_ARTICLES.slice(0, 6).map(toLink)],
  },
  {
    label: 'FAQs',
    to: '/faqs',
    children: [
      { label: 'All FAQs', to: '/faqs' },
      { label: 'New to Family Tree Maker', to: '/faqs#general' },
      { label: 'Questions About FTM 2024', to: '/faqs#ftm-2024' },
      { label: 'Syncing, Photos & Cloud Storage', to: '/faqs#sync' },
      { label: 'Setup, Upgrades & New Computers', to: '/faqs#install' },
      { label: 'About Our Help Center', to: '/faqs#help-desk' },
    ],
  },
  { label: 'Blogs', to: '/blogs' },
];

export const VERSION_TABLE = [
  { version: 'Family Tree Maker 1.0', released: '1989', note: 'Where it all began' },
  { version: 'FTM 16', released: '2006', note: 'Last of the classic line' },
  { version: 'FTM 2008', released: '2007', note: 'Rebuilt interface' },
  { version: 'FTM 2010', released: '2009', note: 'Speed and stability' },
  { version: 'FTM 2011', released: '2010', note: 'Refined workspaces' },
  { version: 'FTM Mac 2010', released: '2010', note: 'First modern Mac edition' },
  { version: 'FTM 2012', released: '2011', note: 'TreeSync with Ancestry' },
  { version: 'FTM 2014', released: '2013', note: 'Expanded media tools' },
  { version: 'FTM 2017', released: '2017', note: 'FamilySync arrives' },
  { version: 'FTM 2019', released: '2019', note: 'Color coding & photo tools' },
  { version: 'FTM 2024', released: '2024', note: 'Current edition' },
];

export const FAQ_GROUPS = [
  {
    id: 'general',
    title: 'New to Family Tree Maker',
    items: [
      {
        q: 'What exactly is Family Tree Maker?',
        a: 'It is a genealogy program you install on a Windows PC or a Mac. Everything you record, from names and dates to photos and source notes, is saved in a tree file on your own machine rather than on a website.',
      },
      {
        q: 'Do I have to be online to work on my tree?',
        a: 'Only occasionally. Typing in relatives, attaching pictures, and printing charts all work offline. You will want a connection when you first activate the program, install updates, sync with Ancestry, or look at record hints.',
      },
      {
        q: 'Is there a phone or tablet version?',
        a: 'Family Tree Maker itself runs on desktop and laptop computers only. Many people sync their tree to Ancestry so they can glance at it on a phone or tablet through the Ancestry app, then do the heavy editing back at the computer.',
      },
      {
        q: 'How is it different from keeping my tree on Ancestry?',
        a: 'An Ancestry tree lives on Ancestry’s servers and is edited in a web browser. A Family Tree Maker tree lives on your hard drive and is edited in the program. FamilySync can keep the two copies matched if you want the benefits of both.',
      },
      {
        q: 'Who makes the software?',
        a: 'Family Tree Maker is developed and sold by Software MacKiev. We are a separate, independent support service and have no business relationship with them.',
      },
      {
        q: 'I have a tree from another genealogy program. Can I bring it in?',
        a: 'Usually, yes. Export a GEDCOM file from the old program, then import it into Family Tree Maker. Names, dates, and relationships come across well; photos and some custom fields may need tidying afterwards.',
      },
      {
        q: 'What is the best habit a beginner can build?',
        a: 'Record where every fact came from as you enter it. A birth date with a source attached is worth far more than one without, and adding citations later is much harder than doing it straight away.',
      },
    ],
  },
  {
    id: 'ftm-2024',
    title: 'Questions About FTM 2024',
    items: [
      {
        q: 'Is FTM 2024 a big change from earlier editions?',
        a: 'Not in how it looks or feels. The workspaces are laid out the same way as FTM 2017 and 2019, so existing users settle in quickly. The main benefits are compatibility with current operating systems and continued updates.',
      },
      {
        q: 'Where do I find program updates?',
        a: 'Open Family Tree Maker, go to the Help menu, and check for updates. Installing the newest build fixes many crashes and sync problems before you need to call anyone.',
      },
      {
        q: 'Will FTM 2024 open the tree I built in 2019?',
        a: 'Yes. On first open, FTM 2024 converts the older file into its own format. Make a backup in the old version beforehand so you always have an untouched copy to fall back on.',
      },
      {
        q: 'Should I delete my old version after upgrading?',
        a: 'Wait until you have opened your trees in 2024, checked that people and photos look right, and confirmed any sync is working. Removing the old program does not delete tree files, but there is no rush.',
      },
      {
        q: 'Does FTM 2024 work on Apple silicon Macs?',
        a: 'It is designed for current Macs, including models with Apple silicon. Always check the publisher’s latest system requirements if you are running a brand-new macOS release.',
      },
    ],
  },
  {
    id: 'sync',
    title: 'Syncing, Photos & Cloud Storage',
    items: [
      {
        q: 'My sync has stopped part-way. What should I check first?',
        a: 'Look at three things: a steady internet connection, the newest program update, and whether antivirus or firewall software is blocking Family Tree Maker. Editing the same person on the website and on your desktop between syncs can also cause conflicts.',
      },
      {
        q: 'Why do some photos show a broken picture icon?',
        a: 'Family Tree Maker remembers where each file was stored. If a photo is moved, renamed, or sits on a drive that is not plugged in, the link breaks. Run Find Missing Media in the Media workspace to reconnect it.',
      },
      {
        q: 'What is TreeVault and do I need it?',
        a: 'TreeVault is the publisher’s cloud service for storing copies of your trees online. It is optional, but it gives you an off-site copy in case your computer fails. Check the publisher’s current terms for availability and cost.',
      },
      {
        q: 'If I stop paying for Ancestry, what happens to my tree?',
        a: 'The copy inside Family Tree Maker stays on your computer and keeps working. Only the online Ancestry tree and subscription records are affected, so run a final sync and make a backup before you cancel.',
      },
      {
        q: 'Can you help me relink hundreds of missing photos?',
        a: 'Yes. Large relinking jobs are one of the most common things we help with. A technician can show you how to point Family Tree Maker at the right folders so most files reconnect in one go.',
      },
    ],
  },
  {
    id: 'install',
    title: 'Setup, Upgrades & New Computers',
    items: [
      {
        q: 'How many computers can I install Family Tree Maker on?',
        a: 'Most licenses let you install on more than one computer that you personally own. If activation says the limit has been reached, the publisher can reset it for you.',
      },
      {
        q: 'What should I do before a big Windows or macOS update?',
        a: 'Update Family Tree Maker, finish any sync, and make a backup that includes media. Once the system update is done, open the program and your tree before carrying on with research.',
      },
      {
        q: 'How do I move everything to a new computer?',
        a: 'Create a backup with media on the old machine, copy it to a USB drive, install and activate Family Tree Maker on the new one, then restore the backup. If the tree is synced, you can also download it from Ancestry instead.',
      },
      {
        q: 'How can I keep my FTM 2019 file from getting damaged?',
        a: 'Always exit the program normally, let syncs finish, keep the tree on your computer’s internal drive rather than a USB stick or cloud folder, compact it every so often, and back up at the end of each session.',
      },
      {
        q: 'The installer says I need administrator rights. What does that mean?',
        a: 'Windows and macOS only let certain accounts install software. Sign in with the main account on the computer, or right-click the installer on Windows and choose Run as administrator.',
      },
    ],
  },
  {
    id: 'help-desk',
    title: 'About Our Help Center',
    items: [
      {
        q: 'Are you part of Family Tree Maker or Ancestry?',
        a: 'No. We are an independent technical support service. The publisher offers its own official help on its website, and you are always free to use that instead.',
      },
      {
        q: 'How do I reach a technician?',
        a: `Phone ${SUPPORT_PHONE_LABEL}, tap the Live Chat button on any page, or leave your details on the Contact Us page and we will get back to you.`,
      },
      {
        q: 'Do I pay for your guides?',
        a: 'No. Every article and FAQ on this website is free to read. Personal one-to-one sessions may be chargeable, and we will always tell you the price before any work begins.',
      },
      {
        q: 'Is remote access safe?',
        a: 'A technician can only view your screen after you give permission. You can watch everything that happens and end the session at any moment.',
      },
      {
        q: 'What information speeds things up?',
        a: 'Tell us your Family Tree Maker edition, whether you use Windows or a Mac, the exact words of any error message, and when you last made a backup.',
      },
      {
        q: 'What if my problem cannot be fixed?',
        a: 'If a paid issue is not resolved, you can ask for a refund as described in our Refund Policy.',
      },
    ],
  },
];

export const HOME_FAQS = FAQ_GROUPS.flatMap((group) => group.items.slice(0, 2));

export const ABOUT_PARAGRAPHS = [
  `${BRAND_NAME} is a small, independent team of technicians who enjoy genealogy almost as much as fixing computers. We look after the technical side so you can spend your time on the fun part: finding your people.`,
  'Our day is spent untangling the problems that stop family historians in their tracks: installers that refuse to finish, syncs that stall at 40 percent, photos that vanish after a computer upgrade, and trees that suddenly will not open.',
  'We are not the publisher of Family Tree Maker and we do not sell the software. What we offer is patient, practical help by phone and live chat for anyone using Family Tree Maker on Windows or Mac, whatever edition they own.',
];

export const DISCLAIMER_TEXT = `${SITE_DOMAIN} is an independent technical support provider. We are not owned by, partnered with, or endorsed by Software MacKiev, Ancestry.com, FamilySearch, or any other company whose products are named on this website. Brand names and trademarks belong to their owners and appear here only so visitors know which products we support. Free help may be available from the software publisher, particularly while a product is under warranty, and you can contact them directly through their official website at any time.`;

export const DISCLAIMER_SHORT = `${BRAND_NAME} is an independent support service with no connection to Software MacKiev, Ancestry.com, or other trademark holders. Product names are used for identification only.`;
